import { useState } from 'react';
import { del, errorText, get, post } from './api.js';
import { locale, t } from './i18n.js';
import { Link } from './router.jsx';
import { useAuth } from './session.jsx';
import { responseText, sourceText } from './cards.jsx';
import { Field, L, Loadable, Pager, Stars, VerifiedPill, useLoad, useToast } from './ui.jsx';

const monthYear = (iso) => new Date(iso).toLocaleDateString(locale(), { month: 'long', year: 'numeric' });
const STAR_LABEL = ['', t('แย่มาก', 'Terrible'), t('ไม่ดี', 'Poor'), t('พอใช้', 'Fair'), t('ดี', 'Good'), t('ดีมาก', 'Excellent')];
const WHY_NOT = {
  own_profile: t('นี่คือโปรไฟล์ของคุณ รีวิวจะมาจากผู้ที่เคยติดต่อคุณ', 'This is your profile. Reviews come from people who have contacted you'),
  lister_not_verified: t('ผู้ลงประกาศรายนี้ยังไม่ได้ยืนยันตัวตน จึงยังรับรีวิวไม่ได้', 'This lister has not verified their identity yet, so cannot receive reviews'),
  no_contact: t('รีวิวได้หลังจากที่คุณติดต่อผู้ลงประกาศรายนี้ และผู้ลงประกาศตอบรับการติดต่อแล้ว', 'You can review after you contact this lister and they respond'),
  already_reviewed: t('คุณรีวิวผู้ลงประกาศรายนี้แล้ว ลบรีวิวเดิมก่อนถ้าต้องการเขียนใหม่', 'You have already reviewed this lister. Delete your review first to write a new one'),
  not_allowed: '',
};

// Five radio buttons: works with a mouse, touch, Tab and the arrow keys.
function StarPicker({ value, onChange }) {
  const onKey = (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); onChange(Math.min(5, (value || 0) + 1)); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); onChange(Math.max(1, (value || 2) - 1)); }
  };
  return (
    <div className="starpick" role="radiogroup" aria-label={t('ให้คะแนน', 'Rating')} onKeyDown={onKey}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} role="radio" aria-checked={value === n} aria-label={t(`${n} ดาว ${STAR_LABEL[n]}`, `${n} star${n > 1 ? 's' : ''}, ${STAR_LABEL[n]}`)} tabIndex={value === n || (!value && n === 1) ? 0 : -1} className={n <= value ? 'on' : undefined} onClick={() => onChange(n)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9l-5.3 2.8 1.1-5.9L3.5 9.700l5.9-.8z" /></svg>
        </button>
      ))}
      <span className="muted small">{value ? STAR_LABEL[value] : t('แตะดาวเพื่อให้คะแนน', 'Tap a star to rate')}</span>
    </div>
  );
}

function ReviewForm({ listerId, onDone }) {
  const toast = useToast();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const text = comment.trim();
    const errs = { rating: rating ? '' : t('เลือกคะแนน 1 ถึง 5 ดาว', 'Choose a rating from 1 to 5 stars'), comment: text.length >= 10 ? '' : t('เขียนอย่างน้อย 10 ตัวอักษร', 'Write at least 10 characters') };
    setErrors(errs);
    if (errs.rating || errs.comment) return;
    setBusy(true);
    try { await post(`/v1/listers/${listerId}/reviews`, { rating, comment: text }); toast(t('ส่งรีวิวแล้ว ขอบคุณที่ช่วยให้ข้อมูลกับผู้ใช้คนอื่น', 'Review submitted. Thank you for helping other users')); onDone(); }
    catch (er) { toast(er.code === 'conflict' ? t('คุณรีวิวผู้ลงประกาศรายนี้แล้ว', 'You have already reviewed this lister') : errorText(er)); } finally { setBusy(false); }
  };
  return (
    <form className="card stack" onSubmit={submit} noValidate>
      <h2>{t('เขียนรีวิว', 'Write a review')}</h2>
      <div className="f"><span className="lab">{t('คะแนนโดยรวม', 'Overall rating')}</span><StarPicker value={rating} onChange={(n) => { setRating(n); setErrors((x) => ({ ...x, rating: '' })); }} />{errors.rating && <span className="err">{errors.rating}</span>}</div>
      <Field label={t('เล่าประสบการณ์ของคุณ', 'Tell us about your experience')} id="rv-comment" error={errors.comment} hint={t(`${comment.trim().length} / 1000 ตัวอักษร รีวิวแสดงต่อสาธารณะพร้อมชื่อต้นของคุณ`, `${comment.trim().length} / 1000 characters. Reviews are public and show your first name`)}>
        <textarea id="rv-comment" maxLength={1000} value={comment} onChange={(e) => { setComment(e.target.value); if (e.target.value.trim().length >= 10) setErrors((x) => ({ ...x, comment: '' })); }} placeholder={t('เช่น ตอบเร็วไหม ข้อมูลตรงกับประกาศไหม การนัดชมเป็นอย่างไร', 'For example: did they reply quickly, did the details match the listing, how was the viewing?')} />
      </Field>
      <button className="btn primary" type="submit" disabled={busy}>{t('ส่งรีวิว', 'Submit review')}</button>
    </form>
  );
}

function ReviewItem({ r, canReply, onChange }) {
  const toast = useToast();
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');
  const send = async (e) => {
    e.preventDefault();
    if (reply.trim().length < 2) return;
    try { await post(`/v1/reviews/${r.id}/reply`, { reply: reply.trim() }); toast(t('ตอบกลับแล้ว', 'Reply sent')); setReplying(false); onChange(); } catch (er) { toast(errorText(er)); }
  };
  const remove = async () => { try { await del(`/v1/reviews/${r.id}`); toast(t('ลบรีวิวแล้ว', 'Review deleted')); onChange(); } catch (er) { toast(errorText(er)); } };
  return (
    <article className="review">
      <header><span className="av">{r.reviewer.initial}</span><div><strong>{r.reviewer.first_name}</strong><span className="muted small">{monthYear(r.created_at)}</span></div></header>
      <Stars value={r.rating} />
      <p>{r.comment}</p>
      {r.reply && <div className="reply"><strong className="small">{t('คำตอบจากผู้ลงประกาศ', 'Reply from the lister')}</strong><p>{r.reply}</p></div>}
      {canReply && !r.reply && (replying
        ? <form className="stack" onSubmit={send}><textarea aria-label={t('คำตอบของคุณ', 'Your reply')} maxLength={1000} value={reply} onChange={(e) => setReply(e.target.value)} style={{ minHeight: 80 }} autoFocus />
          <div className="row"><button className="btn primary s" type="submit">{t('ส่งคำตอบ', 'Send reply')}</button><button className="btn ghost s" type="button" onClick={() => setReplying(false)}>{t('ยกเลิก', 'Cancel')}</button></div>
          <span className="muted small">{t('ตอบได้หนึ่งครั้งต่อรีวิว และแก้ไขภายหลังไม่ได้', 'You can reply once per review and cannot edit it later')}</span></form>
        : <div><button className="btn ghost s" onClick={() => setReplying(true)}>{t('ตอบกลับ', 'Reply')}</button></div>)}
      {r.is_mine && <div><button className="btn danger s" onClick={remove}>{t('ลบรีวิวของฉัน', 'Delete my review')}</button></div>}
    </article>
  );
}

/* ---------- public lister profile with reviews ---------- */
export function ListerProfile({ params }) {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const profile = useLoad(() => get(`/v1/listers/${params.id}`), [params.id]);
  const reviews = useLoad(() => get(`/v1/listers/${params.id}/reviews`, { page }), [params.id, page, user?.id]);
  const refresh = () => { profile.reload(); reviews.reload(); };
  const mine = user?.id === params.id;
  return (
    <div className="page" style={{ maxWidth: 760, marginInline: 'auto', width: '100%' }}>
      <Loadable state={profile}>{({ data: p }) => (
        <>
          <div className="card stack">
            <div className="person">
              <span className="av">{(p.agency_name || p.full_name).trim().charAt(0)}</span>
              <div style={{ minWidth: 0 }}><h1>{p.agency_name || p.full_name}</h1><p className="muted small">{L.role[p.role]}{p.agency_name ? ` ${p.full_name}` : ''} • {t('สมาชิกตั้งแต่', 'Member since')} {monthYear(p.member_since)}</p></div>
            </div>
            <div className="row">{p.verified_badge && <VerifiedPill text={t('ยืนยันตัวตนแล้ว', 'Identity verified')} />}<span className="pill">{sourceText({ type: p.source.type })}</span></div>
            <dl className="perf">
              <div><dt>{t('ประกาศออนไลน์', 'Active listings')}</dt><dd className="num">{p.active_listings}</dd></div>
              <div><dt>{t('อัตราการตอบ', 'Response rate')}</dt><dd className="num">{p.response.response_rate != null ? `${p.response.response_rate}%` : '–'}</dd></div>
              <div><dt>{t('เวลาตอบโดยทั่วไป', 'Typical response time')}</dt><dd>{responseText(p.response) || '–'}</dd></div>
              <div><dt>{t('รีวิว', 'Reviews')}</dt><dd className="num">{p.rating_count ? `${p.rating_average.toFixed(1)} (${p.rating_count})` : '–'}</dd></div>
            </dl>
            <div className="row"><Link className="btn ghost s" to={`/search?source=${p.role}`}>{t('ดูประกาศ', 'View listings')}</Link></div>
          </div>
          <section className="card ratebox" aria-label={t('คะแนนรีวิว', 'Review rating')}>
            <div className="ratebig"><b className="num">{p.rating_count ? p.rating_average.toFixed(1) : '–'}</b><Stars value={p.rating_average || 0} /><span className="muted small num">{p.rating_count ? t(`${p.rating_count} รีวิว`, `${p.rating_count} review${p.rating_count === 1 ? '' : 's'}`) : t('ยังไม่มีรีวิว', 'No reviews yet')}</span></div>
            <div className="ratebars num">
              {[5, 4, 3, 2, 1].map((n) => (
                <div key={n}><span>{n}</span><i><b style={{ width: `${p.rating_count ? (p.rating_breakdown[n] / p.rating_count) * 100 : 0}%` }} /></i><span className="muted">{p.rating_breakdown[n]}</span></div>
              ))}
            </div>
          </section>
        </>
      )}</Loadable>
      <Loadable state={reviews}>{({ data, meta }) => (
        <>
          {meta.can_review && <ReviewForm listerId={params.id} onDone={refresh} />}
          {!meta.can_review && meta.cannot_review_reason === 'sign_in' && <div className="note">{t('รีวิวเขียนได้เฉพาะผู้ที่เคยติดต่อผู้ลงประกาศรายนี้จริง', 'Only people who have actually contacted this lister can write a review.')} <Link to={`/login?next=${encodeURIComponent(`/u/${params.id}`)}`}>{t('เข้าสู่ระบบ', 'Sign in')}</Link></div>}
          {!meta.can_review && WHY_NOT[meta.cannot_review_reason] && <div className="note">{WHY_NOT[meta.cannot_review_reason]}</div>}
          <section className="stack">
            <h2>{t('รีวิวจากผู้ที่เคยติดต่อ', 'Reviews from people who made contact')}</h2>
            {data.length === 0 ? <div className="empty"><span>{t('ยังไม่มีรีวิว รีวิวแรกจะมาจากผู้ที่ติดต่อและได้รับการตอบรับจากผู้ลงประกาศรายนี้', 'No reviews yet. The first will come from someone who contacted this lister and got a response.')}</span></div>
              : <div className="stack">{data.map((r) => <ReviewItem key={r.id} r={r} canReply={mine} onChange={refresh} />)}<Pager meta={meta} onPage={setPage} /></div>}
          </section>
        </>
      )}</Loadable>
    </div>
  );
}

/* ---------- directory of identity-verified agents ---------- */
export function Agents() {
  const [page, setPage] = useState(1);
  const state = useLoad(() => get('/v1/listers', { role: 'agent', page }), [page]);
  return (
    <div className="page">
      <div><h1>{t('นายหน้าที่ยืนยันตัวตนแล้ว', 'Verified agents')}</h1><p className="muted">{t('นายหน้าที่ผ่านการยืนยันตัวตนและมีประกาศออนไลน์อยู่', 'Agents who passed identity verification and have listings online')}</p></div>
      <Loadable state={state}>{({ data, meta }) => data.length === 0
        ? <div className="empty"><span>{t('ยังไม่มีนายหน้าที่ยืนยันตัวตนแล้ว', 'No verified agents yet')}</span></div>
        : <><div className="agents">{data.map((a) => (
          <article className="card stack" key={a.id}>
            <div className="person"><span className="av">{(a.agency_name || a.full_name).trim().charAt(0)}</span><div style={{ minWidth: 0 }}><h3>{a.agency_name || a.full_name}</h3><p className="muted small">{a.agency_name ? a.full_name : L.role[a.role]}</p></div></div>
            <div className="row"><VerifiedPill text={t('ยืนยันตัวตนแล้ว', 'Identity verified')} /></div>
            <dl className="perf">
              <div><dt>{t('ประกาศออนไลน์', 'Active listings')}</dt><dd className="num">{a.active_listings}</dd></div>
              <div><dt>{t('อัตราการตอบ', 'Response rate')}</dt><dd className="num">{a.response.response_rate != null ? `${a.response.response_rate}%` : '–'}</dd></div>
              <div><dt>{t('รีวิว', 'Reviews')}</dt><dd className="num">{a.rating_count ? `${a.rating_average.toFixed(1)} (${a.rating_count})` : '–'}</dd></div>
            </dl>
            <Link className="btn ghost" to={`/u/${a.id}`}>{t('ดูโปรไฟล์', 'View profile')}</Link>
          </article>
        ))}</div><Pager meta={meta} onPage={setPage} /></>}</Loadable>
    </div>
  );
}
